import { useEffect, useState } from "react";
import { getRedemptionNetworks, redeemReward } from "../services/rewards.js";

export function RewardShop({xp, items, voter, onSpendXp, onBack}) {
  const [cat, setCat] = useState("All");
  const [redeemed, setRedeemed] = useState(null); // item just redeemed
  const [confirming, setConfirming] = useState(null);
  const [redeeming, setRedeeming] = useState(false);

  const cats = ["All",...new Set(items.map(i=>i.cat))];
  const visible = cat==="All" ? items : items.filter(i=>i.cat===cat);

  // v1.28.3 — top-of-page error banner replaces the modal-local error.
  // The modal closes on success (or on user cancel), so errors rendered
  // inside the modal vanished with it — users on slow networks never
  // saw them. We now persist the error at the top of the shop page so
  // it survives modal close, auto-clearing when the user opens a new
  // item to retry.
  const [topError, setTopError] = useState("");

  // v1.28.3 — airtime network picker state. Fetched on first mount so
  // the modal opens instantly when the user clicks an airtime item.
  // Falls back to an empty list on failure; the confirm button then
  // shows a disabled "Networks unavailable" state so users don't submit
  // a redemption that would 400 on NETWORK_REQUIRED.
  const [networks, setNetworks] = useState([]);
  const [networksLoading, setNetworksLoading] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState("");

  useEffect(() => {
    // Only fetch if the shop contains at least one Airtime reward —
    // voucher-only shops don't need the network list.
    if (!items.some(i => i.cat === "Airtime")) return;
    let cancelled = false;
    setNetworksLoading(true);
    getRedemptionNetworks()
      .then(list => {
        if (cancelled) return;
        setNetworks(Array.isArray(list) ? list : []);
      })
      .finally(() => {
        if (!cancelled) setNetworksLoading(false);
      });
    return () => { cancelled = true; };
  }, [items]);

  // When the confirmation modal opens for an airtime item, pre-select the
  // first network so the form is valid by default. The user can change
  // the selection before confirming.
  useEffect(() => {
    if (!confirming) { setSelectedNetwork(""); return; }
    if (confirming.cat === "Airtime" && networks.length > 0 && !selectedNetwork) {
      setSelectedNetwork(networks[0].name);
    }
  }, [confirming, networks, selectedNetwork]);

  const openConfirm = (item) => {
    // Clear any prior error banner when starting a new attempt.
    setTopError("");
    setConfirming(item);
  };

  const redeem = async (item) => {
    // EMAIL-9: Airtime rewards require a phone number
    if (item.cat === "Airtime" && !voter?.phone) {
      setTopError("Add your phone number in Profile to redeem airtime.");
      setConfirming(null);
      return;
    }
    // v1.28.3 — airtime also requires a network selection. The UI
    // disables the confirm button in this state, so this is a belt-
    // and-braces guard for keyboard users who hit Enter.
    if (item.cat === "Airtime" && !selectedNetwork) {
      setTopError("Please pick a network before confirming.");
      return;
    }
    setRedeeming(true);
    setTopError("");
    try {
      const networkArg = item.cat === "Airtime" ? selectedNetwork : undefined;
      await redeemReward(item.id, voter?.phone || undefined, networkArg);
      onSpendXp(item.cost);
      setConfirming(null);
      setRedeemed(item);
      setTimeout(() => setRedeemed(null), 3500);
    } catch (err) {
      // Delivery failed — do NOT deduct XP locally (server didn't charge).
      // Keep the modal open so the user can retry without re-navigating.
      const msg = err?.message ?? "";
      if (msg.includes("delivery failed") || msg.includes("try again")) {
        setTopError("Reward delivery failed — your XP was not charged. Please try again.");
      } else if (msg.includes("NETWORK_REQUIRED") || msg.includes("select a network")) {
        setTopError("Please pick a network before confirming.");
      } else {
        setTopError(msg || "Something went wrong. Please try again.");
      }
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div style={{maxWidth:860,margin:"0 auto",padding:"32px 18px 60px"}}>
      {/* v1.28.3 — top-of-page error banner. Appears ABOVE the header so
          users notice it immediately after a failed redemption, no matter
          where they scroll. Dismissible; auto-clears when the user opens a
          new confirmation modal. */}
      {topError && (
        <div
          role="alert"
          data-testid="reward-shop-top-error"
          style={{
            marginBottom:16,padding:"12px 16px",
            background:"#ef444418",border:"1px solid #ef444450",
            borderRadius:10,display:"flex",alignItems:"center",gap:10,
            color:"#fca5a5",fontSize:13,lineHeight:1.5,
          }}
        >
          <span style={{fontSize:16}}>⚠️</span>
          <span style={{flex:1}}>{topError}</span>
          <button
            onClick={() => setTopError("")}
            aria-label="Dismiss error"
            style={{background:"none",border:"none",color:"#fca5a5",cursor:"pointer",fontSize:18,padding:"0 4px",lineHeight:1}}
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12,marginBottom:8}}>
        <div>
          <button onClick={onBack} style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:13,marginBottom:12,padding:0}}>← Back</button>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.15em",color:"#f59e0b",textTransform:"uppercase",marginBottom:7}}>Reward Shop</div>
          <h1 style={{fontSize:26,fontWeight:900,color:"#f1f5f9",margin:0,fontFamily:"Georgia,serif"}}>Spend Your XP</h1>
        </div>
        <div style={{background:"#0d1724",border:"1px solid #f59e0b40",borderRadius:14,padding:"12px 18px",textAlign:"center",flexShrink:0}}>
          <div style={{fontSize:28,fontWeight:900,color:"#f59e0b",fontFamily:"Georgia,serif",lineHeight:1}}>{xp}</div>
          <div style={{fontSize:10,color:"#64748b",marginTop:3,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase"}}>XP Balance</div>
        </div>
      </div>
      <p style={{color:"#475569",fontSize:13,marginBottom:24,lineHeight:1.6}}>Earn XP by playing Trivia Challenges. Redeem for airtime, vouchers, and more.</p>

      {/* Category filter */}
      <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:22}}>
        {cats.map(c=>(
          <button key={c} onClick={()=>setCat(c)}
            style={{padding:"6px 14px",borderRadius:20,border:`1px solid ${cat===c?"#f59e0b40":"#1e2d45"}`,background:cat===c?"#f59e0b18":"#0d1724",color:cat===c?"#f59e0b":"#64748b",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:cat===c?700:400}}>
            {c}
          </button>
        ))}
      </div>

      {/* Items grid */}
      <div className="shop-grid">
        {visible.map(item=>{
          const canAfford = xp>=item.cost;
          return (
            <div key={item.id} style={{background:"#0d1724",border:`1px solid ${canAfford?"#1e2d45":"#0f1a2a"}`,borderRadius:14,padding:16,display:"flex",flexDirection:"column",gap:8,opacity:canAfford?1:0.55,position:"relative",overflow:"hidden"}}>
              {!canAfford&&<div style={{position:"absolute",top:8,right:8,fontSize:9,color:"#334155",fontWeight:700,background:"#090e1a",border:"1px solid #1e2d45",borderRadius:5,padding:"2px 6px"}}>NEED MORE XP</div>}
              <div style={{fontSize:28}}>{item.icon}</div>
              <div>
                <div style={{color:"#f1f5f9",fontWeight:800,fontSize:14,marginBottom:2}}>{item.name}</div>
                <div style={{color:"#475569",fontSize:11}}>{item.provider}</div>
              </div>
              <div style={{color:"#64748b",fontSize:11,lineHeight:1.5,flex:1}}>{item.desc}</div>
              <button onClick={()=>canAfford&&openConfirm(item)} disabled={!canAfford}
                style={{padding:"9px 0",background:canAfford?"linear-gradient(135deg,#f59e0b,#d97706)":"#1e2d45",color:canAfford?"#000":"#334155",border:"none",borderRadius:9,fontSize:13,fontWeight:700,cursor:canAfford?"pointer":"not-allowed",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                 {item.cost} XP
              </button>
            </div>
          );
        })}
      </div>

      {/* Confirm modal */}
      {confirming && (
        <div style={{position:"fixed",inset:0,background:"#000000bb",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:18,padding:"28px 24px",maxWidth:340,width:"100%",textAlign:"center",animation:"popIn .3s ease"}}>
            <div style={{fontSize:36,marginBottom:10}}>{confirming.icon}</div>
            <h3 style={{color:"#f1f5f9",fontWeight:900,fontFamily:"Georgia,serif",fontSize:18,margin:"0 0 6px"}}>{confirming.name}</h3>
            <p style={{color:"#64748b",fontSize:13,margin:"0 0 20px",lineHeight:1.6}}>{confirming.desc}</p>
            <div style={{background:"#090e1a",border:"1px solid #1e2d45",borderRadius:9,padding:"10px 14px",marginBottom:18,fontSize:13}}>
              <span style={{color:"#64748b"}}>Cost: </span>
              <span style={{color:"#f59e0b",fontWeight:700}}> {confirming.cost} XP</span>
              <span style={{color:"#334155",margin:"0 8px"}}>·</span>
              <span style={{color:"#64748b"}}>Remaining: </span>
              <span style={{color:"#94a3b8",fontWeight:700}}> {xp-confirming.cost} XP</span>
            </div>

            {/* v1.28.3 — airtime network picker. Only shown for Airtime
                category; voucher/data rewards skip straight to confirm.
                The list is populated from OgaRecharge /service_providers
                at mount so this opens instantly. On fetch failure we
                block confirmation rather than let the server 400 out
                with NETWORK_REQUIRED. */}
            {confirming.cat === "Airtime" && (
              <div style={{textAlign:"left",marginBottom:16}}>
                <label htmlFor="network-select" style={{display:"block",color:"#94a3b8",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>
                  Network
                </label>
                <select
                  id="network-select"
                  data-testid="network-select"
                  value={selectedNetwork}
                  onChange={(e) => setSelectedNetwork(e.target.value)}
                  disabled={networksLoading || networks.length === 0}
                  style={{
                    width:"100%",padding:"10px 12px",
                    background:"#090e1a",border:"1px solid #1e2d45",borderRadius:9,
                    color:"#f1f5f9",fontSize:13,fontFamily:"inherit",cursor:networksLoading?"wait":"pointer",
                  }}
                >
                  {networksLoading && <option value="">Loading networks…</option>}
                  {!networksLoading && networks.length === 0 && (
                    <option value="">Networks unavailable — try later</option>
                  )}
                  {!networksLoading && networks.length > 0 && !selectedNetwork && (
                    <option value="">Select a network…</option>
                  )}
                  {networks.map((n) => (
                    <option key={n.code || n.name} value={n.name}>{n.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{display:"flex",gap:9}}>
              <button onClick={()=>{setConfirming(null);}} style={{flex:1,padding:"11px",background:"transparent",border:"1px solid #1e2d45",borderRadius:9,color:"#64748b",fontSize:13,cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
              <button
                onClick={()=>redeem(confirming)}
                disabled={redeeming || (confirming.cat === "Airtime" && (!selectedNetwork || networks.length === 0))}
                style={{
                  flex:2,padding:"11px",
                  background:redeeming?"#1e2d45":"linear-gradient(135deg,#f59e0b,#d97706)",
                  color:redeeming?"#64748b":"#000",border:"none",borderRadius:9,fontSize:14,fontWeight:700,
                  cursor:redeeming?"not-allowed":"pointer",fontFamily:"inherit",
                  opacity: (confirming.cat === "Airtime" && (!selectedNetwork || networks.length === 0)) ? 0.5 : 1,
                }}
              >
                {redeeming?"Processing…":"Confirm Redeem"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success toast */}
      {redeemed && (
        <div style={{position:"fixed",bottom:30,left:"50%",transform:"translateX(-50%)",zIndex:1001,animation:"slideUp .3s ease",whiteSpace:"nowrap"}}>
          <div style={{background:"#0d1724",border:"1px solid #22c55e40",borderRadius:20,padding:"10px 20px",display:"flex",alignItems:"center",gap:9,boxShadow:"0 4px 24px #00000088"}}>
            <span style={{fontSize:18}}>✅</span>
            <span style={{color:"#f1f5f9",fontSize:13,fontWeight:600}}>{redeemed.name} redeemed!</span>
            <span style={{color:"#475569",fontSize:12}}>Check your {redeemed.provider} account</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default RewardShop;
